/**
 * Mobile Menu Module
 *
 * Handles mobile menu toggle with GSAP animations.
 *
 * @package TheRealEstates
 */

import { gsap } from 'gsap';

export default class MobileMenu {
  constructor() {
    this.toggle = document.querySelector('[data-menu-toggle]');
    this.menu = document.querySelector('[data-mobile-menu]');
    this.overlay = document.querySelector('[data-menu-overlay]');
    this.closeBtn = document.querySelector('[data-menu-close]');

    if (!this.toggle || !this.menu) return;

    this.links = this.menu.querySelectorAll('.mobile-menu__nav a');
    this.isOpen = false;
    this.timeline = null;

    this.init();
  }

  /**
   * Initialize mobile menu
   */
  init() {
    this.createTimeline();
    this.bindEvents();
  }

  /**
   * Create GSAP animation timeline
   */
  createTimeline() {
    this.timeline = gsap.timeline({ paused: true });

    // Set initial states
    gsap.set(this.menu, { display: 'none' });

    this.timeline
      // Show menu container
      .set(this.menu, { display: 'flex' })
      // Animate overlay
      .fromTo(
        this.overlay,
        { opacity: 0, visibility: 'hidden' },
        { opacity: 1, visibility: 'visible', duration: 0.3 }
      )
      // Slide in menu
      .fromTo(
        this.menu,
        { xPercent: 100 },
        { xPercent: 0, duration: 0.5, ease: 'power3.out' },
        '<'
      )
      // Stagger in links
      .fromTo(
        this.links,
        { opacity: 0, x: 20 },
        {
          opacity: 1,
          x: 0,
          duration: 0.3,
          stagger: 0.05,
          ease: 'power2.out',
        },
        '-=0.2'
      );
  }

  /**
   * Bind event listeners
   */
  bindEvents() {
    // Toggle button
    this.toggle.addEventListener('click', () => this.toggleMenu());

    // Close button
    if (this.closeBtn) {
      this.closeBtn.addEventListener('click', () => this.close());
    }

    // Overlay click
    if (this.overlay) {
      this.overlay.addEventListener('click', () => this.close());
    }

    // Close on link click
    this.links.forEach((link) => {
      link.addEventListener('click', () => this.close());
    });

    // Close on escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen) {
        this.close();
      }
    });

    // Close on resize to desktop
    window.addEventListener('resize', () => {
      if (window.innerWidth >= 1024 && this.isOpen) {
        this.close();
      }
    });
  }

  /**
   * Toggle menu state
   */
  toggleMenu() {
    this.isOpen ? this.close() : this.open();
  }

  /**
   * Open menu
   */
  open() {
    if (this.isOpen) return;

    this.isOpen = true;
    this.toggle.classList.add('is-active');
    this.toggle.setAttribute('aria-expanded', 'true');
    this.menu.classList.add('is-open');
    this.overlay?.classList.add('is-open');

    // Prevent body scroll and horizontal overflow
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflowX = 'hidden';

    // Play animation
    this.timeline.play();

    // Trap focus in menu
    this.trapFocus();
  }

  /**
   * Close menu
   */
  close() {
    if (!this.isOpen) return;

    this.isOpen = false;
    this.toggle.classList.remove('is-active');
    this.toggle.setAttribute('aria-expanded', 'false');

    // Reverse animation
    this.timeline.reverse().then(() => {
      this.menu.classList.remove('is-open');
      this.overlay?.classList.remove('is-open');
    });

    // Restore body scroll
    document.body.style.overflow = '';
    document.documentElement.style.overflowX = '';

    // Restore focus to toggle
    this.toggle.focus();
  }

  /**
   * Trap focus within menu when open
   */
  trapFocus() {
    const focusableElements = this.menu.querySelectorAll(
      'a[href], button, textarea, input, select, [tabindex]:not([tabindex="-1"])'
    );

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    this.menu.addEventListener('keydown', (e) => {
      if (e.key !== 'Tab') return;

      if (e.shiftKey) {
        if (document.activeElement === firstElement) {
          e.preventDefault();
          lastElement.focus();
        }
      } else {
        if (document.activeElement === lastElement) {
          e.preventDefault();
          firstElement.focus();
        }
      }
    });

    // Focus first element
    firstElement?.focus();
  }

  /**
   * Destroy module
   */
  destroy() {
    this.timeline?.kill();
    this.close();
  }
}
